import type { MaybeRef, MaybeRefOrGetter, Ref } from "vue"
import type { IPagination } from "@/types"
import { computed, ref, toValue, watchEffect } from "vue"

export interface UsePaginationOptions {
  initialPage?: MaybeRef<number | undefined>
  initialPerPage?: MaybeRef<number | undefined>
  pageCount?: Ref<number>
  scrollStartRef?: Ref<HTMLElement | null>
  loop?: MaybeRefOrGetter<boolean>
}

export function usePagination({
  initialPerPage,
  pageCount,
  initialPage,
  scrollStartRef,
  loop,
}: UsePaginationOptions = {}): IPagination {
  const page = ref<number>(1)
  const perPage = ref<number>(5)

  watchEffect(() => {
    if (initialPage) {
      page.value = toValue(initialPage) ?? 1
    }
  })

  watchEffect(() => {
    if (initialPerPage) {
      perPage.value = toValue(initialPerPage) ?? 5
    }
  })

  const canNext = computed<boolean>(() => {
    const _pageCount = toValue(pageCount)

    if (!_pageCount)
      return false

    const next = toValue(page) + 1
    return Boolean(next > 0 && next !== _pageCount + 1)
  })

  const canPrevious = computed<boolean>(() => {
    const previous = toValue(page) - 1
    return Boolean(previous > 0)
  })

  const next = () => {
    const pc = toValue(pageCount)
    if (pc && page.value + 1 > pc && toValue(loop)) {
      page.value = 1
      return
    }

    if (canNext.value) {
      page.value++
      scrollStartRef?.value?.scrollIntoView({ behavior: "instant", block: "start" })
    }
  }

  const previous = () => {
    const pc = toValue(pageCount)
    if (pc && page.value - 1 <= 0 && toValue(loop)) {
      page.value = pc
      return
    }

    if (canPrevious.value) {
      page.value--
      scrollStartRef?.value?.scrollIntoView({ behavior: "instant", block: "start" })
    }
  }

  return {
    page,
    perPage,
    canNext,
    canPrevious,
    pageCount,
    next,
    previous,
  }
}
